import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Search, Plus } from 'lucide-react';
import { useUsersList, useCreateUser, type UserListStatus } from '@/features/users/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Segmented } from '@/components/ui/Segmented';
import type { AdminDeviceSessionRow, CreateUserInput } from '@honeycomb/shared';

export const Route = createFileRoute('/_authed/users/')({
  component: UsersPage,
});

function UsersPage() {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<UserListStatus>('active');
  const trimmed = query.trim();
  const { data, isLoading } = useUsersList({ search: trimmed || undefined, status });
  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const navigate = useNavigate();

  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">Users</h1>
          {!isLoading && (
            <p className="text-ink-soft text-sm mt-1">{total} user{total === 1 ? '' : 's'}</p>
          )}
        </div>
        <Button
          data-testid="users-new-button"
          onClick={() => setCreateOpen(true)}
        >
          <Plus size={16} className="mr-1" />
          New user
        </Button>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
            <Search size={16} />
          </div>
          <Input
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="users-search-input"
          />
        </div>
        <div data-testid="users-status-filter">
          <Segmented<UserListStatus>
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: 'Active', testId: 'users-status-active' },
              { value: 'inactive', label: 'Inactive', testId: 'users-status-inactive' },
              { value: 'all', label: 'All', testId: 'users-status-all' },
            ]}
          />
        </div>
      </div>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-6 text-ink-soft">
            {trimmed
              ? 'No matches.'
              : status === 'inactive'
                ? 'No deleted users.'
                : 'No users yet.'}
          </div>
        ) : (
          <>
            {/* Desktop table — md and above */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Usage</th>
                    <th className="px-4 py-3">Version</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      data-testid={`user-row-desktop-${u.id}`}
                      className="border-b last:border-b-0 hover:bg-cream-deep/60"
                      style={{ borderColor: 'var(--color-hairline)' }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link
                            to="/users/$id"
                            params={{ id: u.id }}
                            className="font-medium hover:underline"
                          >
                            {u.displayName}
                          </Link>
                          {u.deletedAt && (
                            <Badge tone="neutral" data-testid={`user-inactive-badge-${u.id}`}>
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <div className="font-mono text-xs text-ink-faint break-all">{u.id}</div>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{u.email}</td>
                      <td className="px-4 py-3">
                        {u.role === 'admin' ? (
                          <Badge tone="honey">Admin</Badge>
                        ) : (
                          <Badge tone="neutral">User</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {u.entitlement.plan === 'plus' ? (
                          <Badge tone="honey">Plus</Badge>
                        ) : (
                          <Badge tone="neutral">Free</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-ink-soft tabular-nums">
                          {u.entitlement.used} / {u.entitlement.limit === -1 ? '∞' : u.entitlement.limit}
                        </div>
                        <div className="mt-1">
                          <Link
                            to="/users/$id"
                            params={{ id: u.id }}
                            className="text-xs text-honey underline"
                          >
                            Manage →
                          </Link>
                        </div>
                      </td>
                      <td
                        className="px-4 py-3 text-ink-soft text-xs"
                        data-testid={`user-list-version-cell-${u.id}`}
                      >
                        {formatVersionSummary(u.currentVersions)}
                      </td>
                      <td className="px-4 py-3 text-ink-soft font-mono text-xs">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`user-row-edit-desktop-${u.id}`}
                          onClick={() => navigate({ to: '/users/$id', params: { id: u.id } })}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — below md */}
            <ul
              className="md:hidden divide-y"
              style={{ borderColor: 'var(--color-hairline)' }}
            >
              {users.map((u) => (
                <li key={u.id} data-testid={`user-row-mobile-${u.id}`} className="px-4 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate">{u.displayName}</div>
                      {u.deletedAt && <Badge tone="neutral">Inactive</Badge>}
                    </div>
                    <div className="text-ink-soft text-sm truncate mt-0.5">{u.email}</div>
                    <div className="flex items-center gap-2 mt-2">
                      {u.role === 'admin' ? (
                        <Badge tone="honey">Admin</Badge>
                      ) : (
                        <Badge tone="neutral">User</Badge>
                      )}
                      {u.entitlement.plan === 'plus' ? (
                        <Badge tone="honey">Plus</Badge>
                      ) : (
                        <Badge tone="neutral">Free</Badge>
                      )}
                      <span className="text-ink-soft font-mono text-xs tabular-nums">
                        {u.entitlement.used} / {u.entitlement.limit === -1 ? '∞' : u.entitlement.limit}
                      </span>
                      <span className="text-ink-faint font-mono text-xs">
                        {formatDate(u.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-none">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`user-row-edit-mobile-${u.id}`}
                      onClick={() => navigate({ to: '/users/$id', params: { id: u.id } })}
                    >
                      Edit
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {createOpen && (
        <CreateUserDialog onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

interface CreateUserDialogProps {
  onClose: () => void;
}

function CreateUserDialog({ onClose }: CreateUserDialogProps) {
  const create = useCreateUser();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [error, setError] = useState<string | null>(null);

  // Basic client-side validation
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isPasswordValid = password.length >= 8;
  const isDisplayNameValid = displayName.trim().length > 0;
  const isSubmitDisabled =
    create.isPending || !isEmailValid || !isPasswordValid || !isDisplayNameValid;

  const handleSubmit = async () => {
    setError(null);
    const body: CreateUserInput = { email, password, displayName: displayName.trim(), role };
    try {
      await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  };

  return (
    <Dialog open testId="create-user-dialog" title="New user" onClose={onClose}>
      <div className="flex flex-col gap-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="create-user-email"
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="create-user-password"
        />
        <Input
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="create-user-displayname"
        />

        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Role</div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'user' | 'admin')}
            data-testid="create-user-role"
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            style={{ borderColor: 'var(--color-hairline)' }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>

        {error && (
          <div
            className="text-xs rounded-lg p-3"
            data-testid="create-user-error"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            data-testid="create-user-submit"
          >
            {create.isPending ? 'Creating…' : 'Create user'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/**
 * Renders a compact multi-platform version summary, e.g.
 * "iOS 1.0.5 · Android 1.0.4 · Web"
 */
function formatVersionSummary(versions: AdminDeviceSessionRow[]): string {
  const parts: string[] = [];
  const ios = versions.find((v) => v.platform === 'ios');
  const android = versions.find((v) => v.platform === 'android');
  const web = versions.find((v) => v.platform === 'web');
  if (ios) parts.push(`iOS ${ios.app_version}`);
  if (android) parts.push(`Android ${android.app_version}`);
  if (web) parts.push('Web');
  return parts.join(' · ');
}
